import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../context/UserContext";

/**
 * ReadOnlyBanner — Displays a persistent banner when the account owner
 * is viewing a member's data in read-only mode.
 *
 * Usage: Drop <ReadOnlyBanner /> at the top of any page/component that
 * should indicate read-only status (Dashboard, Analysis results, Reports).
 *
 * Also exports useReadOnly() hook for components that need to conditionally
 * disable interactive elements.
 */

export const useReadOnly = () => {
  const {
    isReadOnlyView,
    memberConsentStatus,
    exitReadOnlyView,
    selectedUser,
  } = useUser();
  return {
    isReadOnlyView,
    memberConsentStatus,
    exitReadOnlyView,
    selectedUser,
  };
};

const ReadOnlyBanner = ({ showReturnButton = true, onReturn }) => {
  const { isReadOnlyView, exitReadOnlyView, selectedUser } = useUser();

  if (!isReadOnlyView) return null;

  const memberName = selectedUser
    ? `${selectedUser.firstName} ${selectedUser.lastName}`
    : "member";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 16px",
        marginBottom: 12,
        borderLeft: "4px solid var(--info-color, #17a2b8)",
        backgroundColor: "var(--info-bg, rgba(23, 162, 184, 0.1))",
        borderRadius: 6,
        fontSize: "0.9em",
      }}
      role="status"
      aria-live="polite"
    >
      <span>
        <FontAwesomeIcon icon={faEyeSlash} style={{ marginRight: 8 }} />
        <strong>Read-Only</strong> — Viewing {memberName}&apos;s data. No
        changes can be made.
      </span>
      {showReturnButton && (
        <button
          className="common-button small secondary"
          onClick={() => {
            exitReadOnlyView();
            if (onReturn) onReturn();
          }}
          style={{ marginLeft: 12, whiteSpace: "nowrap" }}
        >
          Return to MyProfile
        </button>
      )}
    </div>
  );
};

export default ReadOnlyBanner;
